import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { default as axios } from "axios";
import Router from "next/router";
import { toaster } from "@/Components/toast";
import { getAppMeta } from "@/helper/helper";
import { logoutFromInterceptor } from "@/contexts/AuthContext";

const PASSWORD_EXPIRY_WARNING_HEADER = "x-password-expiry-warning";
const USER_AGENT_SYNC_INTERVAL_MS = 60 * 1000;
const ACTIVITY_UPDATE_INTERVAL_MS = 5 * 60 * 1000;

let userAgentSyncInFlight = false;
let lastActivitySyncInFlight = false;

const checkAndSyncLastActivity = async (response: AxiosResponse) => {
  if (typeof window === "undefined" || lastActivitySyncInFlight) return;

  const token = localStorage.getItem("token");
  const baseURL = response.config.baseURL;
  const requestUrl = String(response.config.url || "");

  if (
    localStorage.getItem("isImpersonationLogin") === "true" ||
    !token ||
    !baseURL ||
    requestUrl.includes("/users/logout") ||
    requestUrl.includes("/users/update-user-agent") ||
    requestUrl.includes("/users/update-last-activity") ||
    requestUrl.includes("/auth/refresh-token")
  ) {
    return;
  }

  const nextActivityUpdateAt = Number(
    localStorage.getItem("nextActivityUpdateAt") || 0
  );
  if (Date.now() <= nextActivityUpdateAt) return;

  lastActivitySyncInFlight = true;
  try {
    const updateLastActivityUrl = `${baseURL.replace(
      /\/$/,
      ""
    )}/users/update-last-activity`;

    await axios.post(
      updateLastActivityUrl,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      }
    );

    localStorage.setItem(
      "nextActivityUpdateAt",
      String(Date.now() + ACTIVITY_UPDATE_INTERVAL_MS)
    );
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 || status === 403) {
      localStorage.setItem(
        "nextActivityUpdateAt",
        String(Date.now() + ACTIVITY_UPDATE_INTERVAL_MS)
      );
    } else {
      console.warn(
        "Failed to sync user last activity:",
        status || error?.message || "unknown error"
      );
    }
  } finally {
    lastActivitySyncInFlight = false;
  }
};

const syncAppUserAgentFromActivity = async (response: AxiosResponse) => {
  if (typeof window === "undefined" || userAgentSyncInFlight) return;

  const token = localStorage.getItem("token");
  const baseURL = response.config.baseURL;
  const requestUrl = String(response.config.url || "");

  if (
    localStorage.getItem("isImpersonationLogin") === "true" ||
    !token ||
    !baseURL ||
    requestUrl.includes("/users/logout") ||
    requestUrl.includes("/users/update-user-agent") ||
    requestUrl.includes("/auth/refresh-token")
  ) {
    return;
  }

  const appMeta = getAppMeta();

  const lastSyncAt = Number(localStorage.getItem("lastUserAgentSyncAt") || 0);
  if (Date.now() - lastSyncAt < USER_AGENT_SYNC_INTERVAL_MS) return;

  userAgentSyncInFlight = true;
  try {
    const updateUserAgentUrl = `${baseURL.replace(
      /\/$/,
      ""
    )}/users/update-user-agent`;

    await axios.post(
      updateUserAgentUrl,
      appMeta
        ? {
            pwa: true,
            version: appMeta.version || null,
            osVersion: appMeta.osVersion || null,
            platform: appMeta.platform || null,
          }
        : {
            pwa: false,
            platform: "web",
          },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      }
    );

    localStorage.setItem("pwaUpdated", "true");
    localStorage.setItem("lastUserAgentSyncAt", String(Date.now()));
  } catch (error: any) {
    const status = Number(error?.response?.status || 0);
    // Auth failures are expected on expired sessions; back off and stay quiet so
    // Next.js doesn't surface this background sync as a runtime overlay error.
    if (status === 401 || status === 403) {
      localStorage.setItem("lastUserAgentSyncAt", String(Date.now()));
    } else {
      console.warn(
        "Failed to sync app user agent from activity:",
        status || error?.message || "unknown error"
      );
    }
  } finally {
    userAgentSyncInFlight = false;
  }
};

const showPasswordExpiryWarningOnce = (warningHeader?: string) => {
  if (typeof window === "undefined" || !warningHeader) return;

  try {
    const warning = JSON.parse(warningHeader);
    const userId = localStorage.getItem("userId") || warning?.userId;
    const daysRemaining = Number(warning?.daysRemaining || 0);
    const passwordChangedAt = warning?.passwordChangedAt;

    if (!userId || !passwordChangedAt) return;
    if (daysRemaining <= 0 || daysRemaining > 5) return;

    const today = new Date().toISOString().slice(0, 10);
    const toastKey = `password-expiry-warning-${userId}-${passwordChangedAt}`;

    if (localStorage.getItem(toastKey) === today) return;

    toaster.warn(
      `Your password will expire in ${daysRemaining} day${
        daysRemaining === 1 ? "" : "s"
      }. Please reset it soon.`,
      "Password Expiring Soon"
    );
    localStorage.setItem(toastKey, today);
  } catch (error) {
    console.error("Failed to parse password expiry warning", error);
  }
};

const handleUnauthError = async (error: any) => {
  if (error?.message === "Request failed with status code 403") {
    localStorage.removeItem("token");
    void Router.push("/signin");
  }
};

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const token = localStorage.getItem("token");
  if (!token) {
    localStorage.clear();
    void Router.push("/signin");
    return config;
  }
  config.headers.Authorization = `Bearer ${token}`;
  if (localStorage.getItem("isImpersonationLogin") === "true") {
    config.headers["x-impersonate"] = "true";
  }
  return config;
};

const onRequestError = async (error: any): Promise<AxiosError> => {
  console.error(`\n==> [INTERCEPTOR REQUEST ERROR] [${JSON.stringify(error)}]`);
  void handleUnauthError(error);
  return Promise.reject(
    error instanceof Error ? error : new Error(String(error))
  );
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  showPasswordExpiryWarningOnce(
    response.headers[PASSWORD_EXPIRY_WARNING_HEADER]
  );
  void syncAppUserAgentFromActivity(response);
  void checkAndSyncLastActivity(response);
  return response;
};

const signOut = () => {
  localStorage.clear();
  void Router.push("/signin?resetTheme=1");
};

const rejectAsError = (error: unknown) =>
  Promise.reject(error instanceof Error ? error : new Error(String(error)));

const onResponseError = async (
  error: any,
  axiosInstance: AxiosInstance
): Promise<AxiosResponse> => {
  const statusCode: number = error?.response?.data?.statusCode;
  const url = String(error?.config?.url || "");
  const errorMessage = String(error?.response?.data?.message || "");

  // 403 / 426 → immediate sign-out, no refresh attempt
  if (statusCode === 403 || statusCode === 426) {
    logoutFromInterceptor();
    return rejectAsError(error);
  }

  // // Previous behavior: sign out on any 401
  // if (statusCode === 401 && !url.includes("/users/update-user-agent")) {
  //   signOut();
  //   return Promise.reject(error);
  // }

  // 401 with expired or invalid token → attempt refresh once, then retry original request
  if (
    statusCode === 401 &&
    (errorMessage.includes("Token expired") ||
      errorMessage.includes("Invalid token") ||
      errorMessage.includes("Unauthorized"))
  ) {
    const config = error.config;

    // _retry guard: if the retried request also 401s, session is dead
    if (config._retry) {
      logoutFromInterceptor();
      return rejectAsError(error);
    }
    config._retry = true;

    try {
      // const expiredToken = localStorage.getItem("token") ?? "";
      const expiredToken =
        config.headers?.Authorization ||
        config.headers?.authorization ||
        (localStorage.getItem("token")
          ? `Bearer ${localStorage.getItem("token")}`
          : undefined);
      const appMeta = getAppMeta();
      const res = await axios.post(
        // Raw axios — bypasses interceptors, preventing an infinite loop
        `${String(config.baseURL ?? "").replace(/\/$/, "")}/auth/refresh-token`,
        {},
        {
          headers: {
            Authorization: expiredToken,
            ...(config.headers?.["x-impersonate"] && {
              "x-impersonate": config.headers["x-impersonate"],
            }),
            ...(config.headers?.["organizationid"] && {
              organizationid: config.headers["organizationid"],
            }),
            // commonRequestInterceptor is skipped on this raw axios call, so
            // app-meta headers (needed by the appOnlyAccess check) must be
            // attached manually — otherwise native-app refreshes look like
            // plain browser requests and get rejected.
            ...(appMeta && {
              "x-app-name": appMeta.app,
              "x-app-platform": appMeta.platform,
              "x-app-version": appMeta.version,
              "x-app-build": appMeta.build,
              "x-app-os": appMeta.osVersion,
              "x-request-source": "webview",
            }),
          },
          timeout: 10_000,
        }
      );
      const newToken: string = res.data?.token;
      if (!newToken) throw new Error("No token in refresh response");

      const warningHeader = res.headers[PASSWORD_EXPIRY_WARNING_HEADER];
      if (warningHeader) {
        showPasswordExpiryWarningOnce(warningHeader);
      }

      localStorage.setItem("token", newToken);
      config.headers["Authorization"] = `Bearer ${newToken}`;
      return axiosInstance(config); // retry original request with new token
    } catch (refreshError) {
      signOut();
      return rejectAsError(refreshError);
    }
  }

  // All other 401s (permission denied, wrong org, etc.) → sign out
  if (
    statusCode === 401 &&
    !url.includes("/users/update-user-agent") &&
    !url.includes("/users/update-last-activity")
  ) {
    signOut();
  }

  return rejectAsError(error);
};

export function setupInterceptorsTo(
  axiosInstance: AxiosInstance
): AxiosInstance {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, (error) =>
    onResponseError(error, axiosInstance)
  );
  return axiosInstance;
}
